import { useEffect, useRef } from "react";
import useQueryManager from "@util/hook/useQueryManager";
import { isEqual, isEmpty } from "@util/utils";

/**
 * 依照 schema 的型別對 params 做型別轉換
 * - value 是 null / undefined / "" -> 直接回傳 schema 的預設值
 * - key 不在 schema 裡 -> 原樣保留（無型別可參考）
 * - 其餘依 schema 型別 coerce：number / boolean / Array / string
 * @param {object} params
 * @param {Record<string, any>} [schema={}]
 * @returns {object}
 */
const coerceBySchema = (params, schema) => {
	if (!schema) return params;
	return Object.fromEntries(
		Object.entries(params).map(([key, value]) => {
			const ref = schema[key];

			if (ref === undefined) return [key, value];
			if (value === null || value === undefined || value === "") return [key, ref];

			if (Array.isArray(ref)) {
				const arr = Array.isArray(value) ? value : [value];
				return [key, typeof ref[0] === "number" ? arr.map(Number) : arr];
			}
			if (typeof ref === "number") {
				const n = Number(value);
				return [key, isNaN(n) ? ref : n];
			}
			if (typeof ref === "boolean") {
				return [key, value === "true" || value === true];
			}

			return [key, value];
		}),
	);
};

/**
 * 通用 Query 初始化 Hook（僅處理 URL query 的讀取/初始化/同步）
 * @param {object} options
 * @param {boolean} options.enable 是否啟用 query string
 * @param {object} options.defaultParams 預設查詢參數
 * @param {string[]} [options.stripKeys=['mode']] 需從 URL 同步資料移除的一次性參數
 * @param {(params: object, schema?: object) => object} [options.coerce] 客製轉型函式
 * @returns {{ searchParams: object, setQuery: (filters: object) => void }}
 */
const useQueryInit = ({ enable, defaultParams, stripKeys = ["mode"], coerce = coerceBySchema }) => {
	const { searchParams, setQuery } = useQueryManager();
	const prevRef = useRef({});
	const isSearchEmpty = isEmpty(searchParams);
	const baseQuery = isSearchEmpty ? defaultParams : searchParams;
	const queryToSync = Object.keys(baseQuery || {}).reduce((acc, key) => {
		if (!stripKeys.includes(key)) {
			acc[key] = baseQuery[key];
		}
		return acc;
	}, {});

	useEffect(
		() => {
			if (!enable) return;
			const hasChanged = !isEqual(prevRef.current, queryToSync);
			const shouldResyncWhenUrlEmpty = isSearchEmpty && !isEmpty(queryToSync);
			if (!hasChanged && !shouldResyncWhenUrlEmpty) return;

			setQuery(queryToSync);
			prevRef.current = queryToSync;
		},
		// eslint-disable-next-line
		[enable, defaultParams, searchParams, setQuery],
	);

	const resolvedSearchParams = !enable ? defaultParams : coerce(baseQuery, defaultParams);

	return {
		searchParams: resolvedSearchParams,
		setQuery: enable ? setQuery : () => {},
	};
};

export default useQueryInit;
